"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface MultipleChoiceCardProps {
  data: {
    question: string;
    options: string[];
    correctIndex: number;
    explanation: string;
  };
  onAnswer: (correct: boolean) => void;
}

export function MultipleChoiceCard({ data, onAnswer }: MultipleChoiceCardProps) {
  const { question, options, correctIndex, explanation } = data;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function handleSelect(index: number) {
    if (submitted) return;
    setSelectedIndex(index);
  }

  function handleCheck() {
    if (selectedIndex === null) return;
    setSubmitted(true);
    onAnswer(selectedIndex === correctIndex);
  }

  function getOptionClasses(index: number) {
    if (submitted) {
      if (index === correctIndex) {
        return "bg-green-100 border-green-500 text-green-800";
      }
      if (index === selectedIndex && index !== correctIndex) {
        return "bg-red-100 border-red-500 text-red-800";
      }
      return "border-border bg-card opacity-60";
    }

    if (index === selectedIndex) {
      return "border-primary ring-2 ring-primary/20 bg-card";
    }

    return "border-border bg-card hover:bg-accent";
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-medium mb-4">{question}</p>

      <div className="flex flex-col gap-3">
        {options.map((option, index) => (
          <button
            key={index}
            type="button"
            disabled={submitted}
            onClick={() => handleSelect(index)}
            className={cn(
              "rounded-lg border p-4 text-left transition",
              submitted ? "cursor-default" : "cursor-pointer",
              getOptionClasses(index)
            )}
          >
            {option}
          </button>
        ))}
      </div>

      {submitted && (
        <div className="bg-muted rounded-lg p-4 mt-4">
          <p className="font-medium mb-1">Explanation</p>
          <p className="text-sm leading-relaxed">{explanation}</p>
        </div>
      )}

      {!submitted && (
        <Button
          className="w-full"
          disabled={selectedIndex === null}
          onClick={handleCheck}
        >
          Check
        </Button>
      )}
    </div>
  );
}
